import * as React from "react";
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  countryOf,
  formatNational,
  nationalLength,
  parsePhone,
  toE164,
} from "@taskiq/shared";
import { FilterSelect, type FilterOption } from "./FilterSelect";
import { Input, type InputSize } from "./Field";
import { cn } from "@/lib/cn";

/**
 * A phone number: a country picker and a national number, stored as E.164.
 *
 * ── The dial code is chosen, never typed ────────────────────────────────────
 *
 * The picker owns `+91`; the input holds `9876543210` and nothing else. So a
 * number that begins with a 1 is a number and never a country code, and nothing
 * has to guess a country out of the digits. `@taskiq/shared`'s `phone.ts` owns
 * the parsing, the joining and the grouping mask, and `mobile-app`'s
 * `phone.dart` is a port of it - a number typed here and the same number typed
 * on the handset produce the identical stored string.
 *
 * ── Why this is a `FilterSelect` and not `react-international-phone` ────────
 *
 * Same argument as `TimezoneSelect`, and it applies harder here. The reference
 * product wraps that library, which is a good library; but the Flutter client
 * cannot use it, and a control that behaves one way in the console and another
 * on the phone is the defect the two-clients rule in the root `CLAUDE.md` is
 * about. What the library actually contributes over this file is per-country
 * input masks - and those are 69 strings in `countries.json`, vendored from it,
 * applied by twenty lines both clients share.
 *
 * ── What a caller sees ──────────────────────────────────────────────────────
 *
 * `value` and `onChange` are the **E.164 string**, or `""`. The country and the
 * national digits are this component's business, so no form has to hold two
 * fields for one number.
 */
export function PhoneField({
  id,
  value,
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  inputSize = "md",
  invalid,
  disabled,
  name,
}: {
  id?: string;
  /** E.164, or `""`. */
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: string;
  inputSize?: InputSize;
  invalid?: boolean;
  disabled?: boolean;
  name?: string;
}) {
  const parsed = parsePhone(value, defaultCountry);

  /**
   * The country is state, not derived, for the one case where the value cannot
   * say what it is: an empty field. Deriving it would snap the picker back to
   * the default the moment somebody cleared the digits to retype them, which
   * silently changes the country under a number half-entered.
   */
  const [iso2, setIso2] = React.useState(parsed.iso2);

  /**
   * A value arriving from outside - a form loading a record - wins.
   *
   * **Only when there is one.** An empty value parses to `defaultCountry`, so
   * without that guard picking a country on an empty field is undone on the very
   * next render: the picker emits `""` (a dial code with no number is not a
   * number), this reads `""` back as the default, and the flag snaps to the
   * United States under somebody who has just chosen India.
   *
   * Typing does not come through here either. `onChange` sends E.164 built from
   * this same country, so `parsed.iso2` already equals `iso2`.
   */
  React.useEffect(() => {
    if (value && parsed.iso2 !== iso2) setIso2(parsed.iso2);
  }, [value, parsed.iso2]); // eslint-disable-line react-hooks/exhaustive-deps

  const country = countryOf(iso2);

  const options: FilterOption[] = React.useMemo(
    () =>
      // Alphabetical for reading. `COUNTRIES` is in dial-code order because
      // that is what parsing needs, and it is a nonsense order to scroll.
      [...COUNTRIES]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((entry) => ({
          value: entry.iso2,
          // The flag and the code on the trigger, the name underneath. The name
          // is in the option so the search finds it - "India" and "+91" and
          // "91" all reach the same row, which is what somebody who knows only
          // one of the three needs.
          label: `${flagEmoji(entry.iso2)} +${entry.dial}`,
          hint: entry.name,
        })),
    []
  );

  return (
    <div className={cn("phonefield", disabled && "phonefield--disabled")}>
      <FilterSelect
        variant="field"
        searchable
        label="Country dialling code"
        value={iso2}
        options={options}
        invalid={invalid}
        disabled={disabled}
        searchPlaceholder="Country or code"
        onChange={(next) => {
          if (!next) return;
          setIso2(next);
          // The typed number survives the country change. Somebody correcting
          // the flag on a number they have already entered is the common case,
          // and clearing it would be the last thing they expect.
          onChange(toE164(next, parsed.national));
        }}
      />
      <Input
        id={id}
        name={name}
        type="tel"
        autoComplete="tel-national"
        inputMode="tel"
        inputSize={inputSize}
        invalid={invalid}
        disabled={disabled}
        className="phonefield__number"
        // The mask as the placeholder, so the expected shape is visible before
        // anything is typed rather than only discovered by typing.
        placeholder={country?.format?.replace(/\./g, "0") ?? "Phone number"}
        value={formatNational(iso2, parsed.national)}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, "");
          const cap = nationalLength(iso2);
          // Two digits past the mask, not a hard stop at it. Masks go stale and
          // some countries genuinely run longer; refusing the digit somebody is
          // trying to type is worse than a slightly odd-looking group.
          onChange(toE164(iso2, cap ? digits.slice(0, cap + 2) : digits.slice(0, 15)));
        }}
      />
    </div>
  );
}

/** 2-letter ISO code → flag emoji (regional indicator letters). */
function flagEmoji(iso2: string): string {
  return iso2.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}
